import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Star, CalendarClock } from "lucide-react";
import { inr } from "../lib/api";
import BookingModal from "./BookingModal";
import ProductVariantPicker from "./ProductVariantPicker";
import { LOW_STOCK_THRESHOLD, useLowStockThreshold } from "./StockBadge";

function FoodMark({ type }) {
    const cls = type === "NON_VEG" ? "non-veg" : type === "EGG" ? "egg" : "veg";
    return <span className={`food-mark ${cls}`} aria-label={type || "VEG"} />;
}

export default function ProductCard({ product, restaurantId, onAdd, isFood = true }) {
    const [busy, setBusy] = useState(false);
    const [qty, setQty] = useState(0);
    const [bookingOpen, setBookingOpen] = useState(false);
    // iter130 — read the tenant-configured low-stock threshold.
    const lowStockThreshold = useLowStockThreshold();
    // Variant picker — opens when the customer taps ADD on a product
    // that has 2+ enabled variants. Single-variant products skip the
    // modal and add directly (no decision to make).
    const [variantPickerOpen, setVariantPickerOpen] = useState(false);
    const enabledVariants = (product?.variants || []).filter(
        (v) => v?.enabled !== false,
    );
    const hasVariantChoice = enabledVariants.length >= 2;
    const isService = product?.kind === "SERVICE";
    // Sold-out detection — server flips status=OUT_OF_STOCK + stock<=0 on
    // the last successful order. When ``track_stock`` is false the
    // merchant explicitly opted out of stock-gating for this item, so we
    // never block ordering even if stock is zero.
    const trackStock = product?.track_stock !== false;
    const isSoldOut = trackStock && (
        product?.status === "OUT_OF_STOCK"
        || (product?.stock != null && Number(product.stock) <= 0)
    );
    // Block adding MORE than is available on the server. ``stock`` is the
    // count remaining, ``qty`` is what the customer has already added in
    // this session — the UI prevents queuing more units than the merchant
    // currently has on hand.
    const remaining = trackStock && product?.stock != null
        ? Math.max(0, Number(product.stock) - qty)
        : Infinity;
    const cantAddMore = remaining <= 0;

    const handleAdd = async (delta, variantId = undefined) => {
        if (delta > 0 && cantAddMore) {
            return;        // silent — the button is disabled, but guard anyway
        }
        // First add of a multi-variant product → open picker, defer the
        // actual cart write until the customer picks a variant.
        if (delta > 0 && qty === 0 && hasVariantChoice && variantId === undefined) {
            setVariantPickerOpen(true);
            return;
        }
        setBusy(true);
        try {
            const newQty = Math.max(0, qty + delta);
            await onAdd?.(product, newQty, qty, variantId);
            setQty(newQty);
        } finally {
            setBusy(false);
        }
    };

    const onVariantPicked = async (variant) => {
        setVariantPickerOpen(false);
        // Hand the chosen variant up to the parent's addToCart so the
        // cart row carries ``variant_id`` and the variant's own price.
        await handleAdd(1, variant.id);
    };

    const BookBtn = (
        <button
            data-testid={`book-now-${product.id}`}
            onClick={() => setBookingOpen(true)}
            className="bg-card text-primary border-2 border-primary font-bold tracking-wider text-xs px-4 py-1.5 rounded-xl shadow-md active:scale-95 transition inline-flex items-center gap-1"
        >
            <CalendarClock size={12} /> BOOK NOW
        </button>
    );

    const SoldOutBadge = (
        <span
            data-testid={`sold-out-${product.id}`}
            className="bg-muted text-muted-foreground border-2 border-border font-bold tracking-wider text-[11px] px-4 py-1.5 rounded-xl"
        >
            SOLD OUT
        </span>
    );

    return (
        <article
            data-testid={`product-card-${product.id}`}
            className={`flex gap-3 py-4 border-b border-border last:border-0 ${isSoldOut ? "opacity-60" : ""}`}
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    {!isService && isFood && <FoodMark type={product.food_type} />}
                    {isService && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1">
                            <CalendarClock size={10} /> Service
                        </span>
                    )}
                    {product.is_bestseller && !isSoldOut && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 inline-flex items-center gap-1">
                            <Star size={10} className="fill-amber-600 text-amber-600" /> Bestseller
                        </span>
                    )}
                    {isSoldOut && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full">
                            Sold out
                        </span>
                    )}
                    {!isSoldOut && trackStock && product?.stock != null && product.stock > 0 && product.stock <= lowStockThreshold && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full" data-testid={`stock-badge-low-${product.id}`}>
                            Only {product.stock} left
                        </span>
                    )}
                </div>
                <h4 className="font-semibold text-[15px] mt-1 line-clamp-1">
                    <Link
                        to={`/product/${product.id}`}
                        data-testid={`pdp-link-title-${product.id}`}
                        className="hover:underline"
                    >
                        {product.name}
                    </Link>
                </h4>
                <div className="flex items-baseline gap-2 mt-1 flex-wrap">
                    {hasVariantChoice ? (
                        <>
                            <span className="text-xs text-muted-foreground">from</span>
                            <span className="font-semibold">
                                {(() => {
                                    // iter134e — filter out variants with a
                                    // null / 0 / missing ``selling_price``
                                    // BEFORE running Math.min. Previously a
                                    // single half-configured variant (price
                                    // still null) dragged the "from" label
                                    // down to ₹0 while the PDP correctly
                                    // showed the real ₹500 of the priced
                                    // variant. Falls back to the product's
                                    // own selling_price if EVERY variant is
                                    // priceless (edge case: merchant added
                                    // variants without filling any price).
                                    const priced = enabledVariants
                                        .map((v) => Number(v.selling_price))
                                        .filter((n) => Number.isFinite(n) && n > 0);
                                    const min = priced.length > 0
                                        ? Math.min(...priced)
                                        : Number(product.selling_price || 0);
                                    return inr(min);
                                })()}
                            </span>
                            {/* iter134c1 — strike-through MRP + discount
                                badge on the variant-list path too. Uses the
                                MRP of the cheapest priced variant so the
                                "from ₹X" price and "was ₹Y — Z% OFF" line
                                up on the same variant. Previously the
                                variant branch had NO discount UI at all
                                (regression report Feb 2026). */}
                            {(() => {
                                const priced = enabledVariants
                                    .map((v) => ({
                                        sel: Number(v.selling_price),
                                        mrp: Number(v.mrp),
                                    }))
                                    .filter((r) => Number.isFinite(r.sel) && r.sel > 0);
                                if (priced.length === 0) return null;
                                const cheapest = priced.reduce(
                                    (a, b) => (a.sel <= b.sel ? a : b),
                                );
                                if (!Number.isFinite(cheapest.mrp) || cheapest.mrp <= cheapest.sel) return null;
                                const pct = Math.round(((cheapest.mrp - cheapest.sel) / cheapest.mrp) * 100);
                                return (
                                    <>
                                        <span
                                            className="text-xs text-muted-foreground line-through"
                                            data-testid={`mrp-${product.id}`}
                                        >
                                            {inr(cheapest.mrp)}
                                        </span>
                                        <span
                                            className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full"
                                            data-testid={`discount-${product.id}`}
                                        >
                                            {pct}% OFF
                                        </span>
                                    </>
                                );
                            })()}
                            <span
                                className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-full"
                                data-testid={`has-variants-${product.id}`}
                            >
                                {enabledVariants.length} options
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="font-semibold">{inr(product.selling_price)}</span>
                            {/* iter134c1 — restore the discount % badge
                                that was rendering in the previous build.
                                The strike-through survived a refactor
                                but the "N% OFF" pill got dropped. */}
                            {product.mrp > product.selling_price && (
                                <>
                                    <span
                                        className="text-xs text-muted-foreground line-through"
                                        data-testid={`mrp-${product.id}`}
                                    >
                                        {inr(product.mrp)}
                                    </span>
                                    <span
                                        className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full"
                                        data-testid={`discount-${product.id}`}
                                    >
                                        {Math.round(((product.mrp - product.selling_price) / product.mrp) * 100)}% OFF
                                    </span>
                                </>
                            )}
                        </>
                    )}
                    {product.unit_of_measure && (
                        <span className="text-xs text-muted-foreground" data-testid={`uom-${product.id}`}>· {product.unit_of_measure}</span>
                    )}
                    {isService && product.service_config?.duration_min && (
                        <span className="text-xs text-muted-foreground">· {product.service_config.duration_min} min</span>
                    )}
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
            </div>
            <div className="relative flex-shrink-0">
                {product.images?.[0] ? (
                    <>
                        <Link
                            to={`/product/${product.id}`}
                            data-testid={`pdp-link-image-${product.id}`}
                            className="block w-24 h-24 rounded-xl overflow-hidden bg-muted"
                        >
                            <img src={product.images[0]} alt={product.name} loading="lazy" className="w-full h-full object-cover" />
                        </Link>
                        {isService ? (
                            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">{BookBtn}</div>
                        ) : isSoldOut ? (
                            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">{SoldOutBadge}</div>
                        ) : qty === 0 ? (
                            <button
                                data-testid={`add-to-cart-${product.id}`}
                                disabled={busy}
                                onClick={() => handleAdd(1)}
                                className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-card text-primary border-2 border-primary font-bold tracking-wider text-xs px-4 py-1.5 rounded-xl shadow-md active:scale-95 transition"
                            >
                                ADD
                            </button>
                        ) : (
                            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-card border-2 border-primary rounded-xl shadow-md flex items-center overflow-hidden">
                                {/* Explicit inline size on the icon — this stepper is
                                    absolutely positioned + shrink-to-fit width (unlike
                                    the no-image branch below, which is a normal in-flow
                                    element and doesn't hit this). The global responsive
                                    rule ``svg { max-width: 100%; height: auto }``
                                    (index.css, iter143) can't resolve a percentage
                                    max-width against the icon's own indeterminate
                                    containing-block width, so it collapses to 0 —
                                    present in the DOM with correct color/paint but
                                    zero-area, so the buttons rendered but the +/- glyphs
                                    were invisible (user-reported: only visible when the
                                    product has no image). ``max-width`` and ``width``
                                    are independent properties that both apply — overriding
                                    just ``width`` isn't enough, since the used width is
                                    ``min(width, max-width)``; ``maxWidth`` must be
                                    overridden too or the 0 still wins. */}
                                <button data-testid={`dec-${product.id}`} disabled={busy} onClick={() => handleAdd(-1)} className="px-2.5 py-1.5 text-primary"><Minus size={14} style={{ width: 14, height: 14, maxWidth: 14 }} /></button>
                                <span className="px-1 text-primary font-bold text-sm min-w-[20px] text-center">{qty}</span>
                                <button data-testid={`inc-${product.id}`} disabled={busy || cantAddMore}
                                    onClick={() => handleAdd(1)}
                                    title={cantAddMore ? `Only ${product.stock} in stock` : undefined}
                                    className="px-2.5 py-1.5 text-primary disabled:opacity-40"><Plus size={14} style={{ width: 14, height: 14, maxWidth: 14 }} /></button>
                            </div>
                        )}
                    </>
                ) : (
                    // No image — render only the action inline (no blank placeholder)
                    isService ? BookBtn : isSoldOut ? SoldOutBadge : qty === 0 ? (
                        <button
                            data-testid={`add-to-cart-${product.id}`}
                            disabled={busy}
                            onClick={() => handleAdd(1)}
                            className="bg-card text-primary border-2 border-primary font-bold tracking-wider text-xs px-5 py-2 rounded-xl shadow-sm active:scale-95 transition"
                        >
                            ADD
                        </button>
                    ) : (
                        <div className="bg-card border-2 border-primary rounded-xl shadow-sm flex items-center overflow-hidden">
                            <button data-testid={`dec-${product.id}`} disabled={busy} onClick={() => handleAdd(-1)} className="px-3 py-2 text-primary"><Minus size={14} /></button>
                            <span className="px-1 text-primary font-bold text-sm min-w-[20px] text-center">{qty}</span>
                            <button data-testid={`inc-${product.id}`} disabled={busy || cantAddMore}
                                onClick={() => handleAdd(1)}
                                title={cantAddMore ? `Only ${product.stock} in stock` : undefined}
                                className="px-3 py-2 text-primary disabled:opacity-40"><Plus size={14} /></button>
                        </div>
                    )
                )}
            </div>
            {bookingOpen && <BookingModal service={product} onClose={() => setBookingOpen(false)} />}
            {variantPickerOpen && (
                <ProductVariantPicker
                    product={product}
                    onConfirm={onVariantPicked}
                    onClose={() => setVariantPickerOpen(false)}
                />
            )}
        </article>
    );
}
